
import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, Search } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

interface Conversation {
  id: string;
  user_id: string;
  alumni_id: string;
  updated_at: string;
  lastMessage?: string;
  otherUser?: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url: string | null;
    user_type: string;
  };
}

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  created_at: string;
  is_read: boolean;
}

const Messages = () => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch current user
  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to access your messages",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      
      const { data: userData, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
        
      if (error) {
        console.error("Error fetching user data:", error);
        return;
      }
      
      setCurrentUser({
        ...userData,
        id: session.user.id
      });
    };
    
    fetchUserData();
  }, [navigate, toast]);

  // Fetch conversations
  const { isLoading: isLoadingConversations } = useQuery({
    queryKey: ["conversations", currentUser?.id],
    enabled: !!currentUser,
    queryFn: async () => {
      const { data: conversationsData, error } = await supabase
        .from("conversations")
        .select("*")
        .or(`user_id.eq.${currentUser.id},alumni_id.eq.${currentUser.id}`);
      
      if (error) {
        throw error;
      }
      
      // Get other user details for each conversation
      const enhancedConversations = await Promise.all(conversationsData.map(async (conv) => {
        const otherUserId = conv.user_id === currentUser.id ? conv.alumni_id : conv.user_id;
        
        // Get other user details
        const { data: userData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", otherUserId)
          .single();
          
        // Get last message
        const { data: lastMessageData } = await supabase
          .from("messages")
          .select("content, created_at")
          .eq("conversation_id", conv.id)
          .order("created_at", { ascending: false })
          .limit(1);
          
        return {
          ...conv,
          otherUser: userData,
          lastMessage: lastMessageData && lastMessageData[0] ? lastMessageData[0].content : ""
        };
      }));
      
      setConversations(enhancedConversations);
      return enhancedConversations;
    }
  });
  
  // Fetch messages for selected conversation
  const { isLoading: isLoadingMessages } = useQuery({
    queryKey: ["messages", selectedConversation?.id],
    enabled: !!selectedConversation,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", selectedConversation.id)
        .order("created_at", { ascending: true });
      
      if (error) {
        throw error;
      }
      
      setMessages(data);
      
      // Mark messages as read
      if (data && data.length > 0) {
        const unreadMessages = data.filter(
          msg => !msg.is_read && msg.receiver_id === currentUser.id
        );
        
        if (unreadMessages.length > 0) {
          const { error: updateError } = await supabase
            .from("messages")
            .update({ is_read: true })
            .in('id', unreadMessages.map(msg => msg.id));
            
          if (updateError) {
            console.error("Error marking messages as read:", updateError);
          }
        }
      }
      
      return data;
    }
  });
  
  // Filter conversations by search query
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    
    const fullName = `${conv.otherUser?.first_name || ''} ${conv.otherUser?.last_name || ''}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) || 
           (conv.lastMessage && conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase()));
  });
  
  // Send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedConversation || !currentUser) return;
    
    try {
      const messageData = {
        conversation_id: selectedConversation.id,
        sender_id: currentUser.id,
        receiver_id: selectedConversation.user_id === currentUser.id 
          ? selectedConversation.alumni_id 
          : selectedConversation.user_id,
        content: newMessage,
        is_read: false
      };
      
      // Insert message
      const { error } = await supabase.from("messages").insert(messageData);
      
      if (error) throw error;
      
      // Update conversation timestamp
      await supabase
        .from("conversations")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", selectedConversation.id);
      
      // Add to local messages
      setMessages(prev => [...prev, {
        ...messageData,
        id: `temp-${Date.now()}`,
        created_at: new Date().toISOString()
      }]);
      
      // Reset input
      setNewMessage("");
      
    } catch (error: any) {
      toast({
        title: "Failed to send message",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Get initials for avatar
  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <div className="min-h-screen flex flex-col bg-white font-montserrat">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Messages</h1>
        
        {!currentUser ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-500">Loading your messages...</p>
          </div>
        ) : (
          <div className="flex h-[calc(100vh-240px)] bg-white rounded-lg shadow-sm border overflow-hidden">
            {/* Conversations sidebar */}
            <div className="w-full sm:w-1/3 lg:w-1/4 border-r">
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    className="pl-9 py-2"
                    placeholder="Search conversations"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="overflow-y-auto h-[calc(100%-64px)]">
                {isLoadingConversations ? (
                  <div className="p-4 text-center">
                    <p className="text-gray-500">Loading conversations...</p>
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className="text-gray-500">No conversations yet</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Start chatting with alumni by visiting their profiles
                    </p>
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                        selectedConversation?.id === conv.id ? "bg-gray-100" : ""
                      }`}
                      onClick={() => setSelectedConversation(conv)}
                    >
                      <div className="flex items-center">
                        <Avatar className="h-10 w-10 mr-3">
                          <AvatarImage src={conv.otherUser?.avatar_url || undefined} />
                          <AvatarFallback className="bg-crimson/10 text-crimson">
                            {getInitials(conv.otherUser?.first_name, conv.otherUser?.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {`${conv.otherUser?.first_name || ''} ${conv.otherUser?.last_name || ''}`}
                            </h3>
                            <span className="text-xs text-gray-500">
                              {new Date(conv.updated_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{conv.lastMessage}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Messages area */}
            <div className="hidden sm:flex sm:w-2/3 lg:w-3/4 flex-col">
              {selectedConversation ? (
                <>
                  {/* Conversation header */}
                  <div className="p-3 border-b flex items-center">
                    <Avatar className="h-8 w-8 mr-3">
                      <AvatarImage src={selectedConversation.otherUser?.avatar_url || undefined} />
                      <AvatarFallback className="bg-crimson/10 text-crimson">
                        {getInitials(
                          selectedConversation.otherUser?.first_name, 
                          selectedConversation.otherUser?.last_name
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-sm font-medium">
                        {`${selectedConversation.otherUser?.first_name || ''} ${selectedConversation.otherUser?.last_name || ''}`}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {selectedConversation.otherUser?.user_type === 'alumni' ? 'Alumni' : 'Student'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Messages */}
                  <div className="flex-1 p-4 overflow-y-auto">
                    {isLoadingMessages ? (
                      <div className="flex justify-center">
                        <p className="text-gray-500">Loading messages...</p>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex justify-center items-center h-full">
                        <div className="text-center">
                          <p className="text-gray-500">No messages yet</p>
                          <p className="text-sm text-gray-400 mt-1">Start the conversation!</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((message) => {
                          const isCurrentUser = message.sender_id === currentUser.id;
                          return (
                            <div 
                              key={message.id} 
                              className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[70%] p-3 rounded-lg ${
                                isCurrentUser 
                                  ? 'bg-crimson text-white rounded-tr-none' 
                                  : 'bg-gray-100 text-gray-800 rounded-tl-none'
                              }`}>
                                <p className="text-sm">{message.content}</p>
                                <p className={`text-xs mt-1 ${
                                  isCurrentUser ? 'text-crimson-light' : 'text-gray-500'
                                }`}>
                                  {new Date(message.created_at).toLocaleTimeString([], { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>
                  
                  {/* Message input */}
                  <div className="p-3 border-t">
                    <form onSubmit={handleSendMessage} className="flex items-center">
                      <Input
                        className="flex-1 mr-2"
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                      />
                      <Button 
                        type="submit" 
                        size="icon" 
                        className="bg-crimson hover:bg-crimson-dark"
                        disabled={!newMessage.trim()}
                      >
                        <Send size={16} />
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-gray-500">Select a conversation</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Choose a conversation from the list to start chatting
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Messages;
